import { useEffect, useState } from "react";
import { Check, Copy, LifeBuoy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { copyText } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useHub } from "@/components/AppShell";
import { Field, PageHeader } from "@/components/common";
import { TimezoneSelect } from "@/components/HubForms";
import { HouseQ, LEGAL_TEXT, Wordmark } from "@/components/brand/Brand";
import FamilySections from "@/components/family/FamilySections";
import Storage from "@/components/family/Storage";
import { usePlan } from "@/components/billing/Plans";
import YourData from "@/components/account/YourData";
import { InboxAddressCard, useInbox } from "@/components/inbox/EmailInbox";

const CYAN = "var(--glow-family)";

function HubDetails({ hub, onSaved }) {
  const [name, setName] = useState(hub.name);
  const [tz, setTz] = useState(hub.timezone);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const ro = !hub.is_parent;

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("Give your hub a name");
    setBusy(true);
    try {
      await api.patch(`/organizations/${hub.id}`, { name, timezone: tz });
      setSaved(true);
      toast.success("Saved");
      onSaved();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="q-card space-y-4" noValidate data-testid="settings-hub-form">
      <h2 className="font-display text-lg font-bold text-q-text">Hub</h2>
      <Field label="Hub name" htmlFor="set-name" error={error} errorTestid="settings-name-error">
        <input id="set-name" className="q-input" value={name} disabled={ro} aria-invalid={!!error} onChange={(e) => { setName(e.target.value); setError(""); setSaved(false); }} data-testid="settings-hub-name-input" />
      </Field>
      <Field label="Time zone" htmlFor="set-tz">
        {ro ? <input className="q-input" disabled value={tz} /> : <TimezoneSelect id="set-tz" value={tz} onChange={(v) => { setTz(v); setSaved(false); }} testid="settings-timezone-select" />}
      </Field>
      {!ro && (
        <div className="flex items-center gap-3">
          <button className="q-btn q-btn-primary" disabled={busy} data-testid="settings-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          {saved && <span className="flex items-center gap-1 text-sm font-semibold" style={{ color: "rgb(var(--glow-money))" }} data-testid="settings-saved"><Check className="h-4 w-4" /> Saved</span>}
        </div>
      )}
    </form>
  );
}

function FamilyCode({ code }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="q-card" data-testid="settings-family-code-card" style={{ background: `radial-gradient(circle at 100% 0%, rgba(${CYAN},0.14), transparent 60%), rgb(var(--q-surface))` }}>
      <h2 className="font-display text-lg font-bold text-q-text">Family code</h2>
      <p className="mt-1 text-sm text-q-muted">Kids type this on "I'm a kid" to find your hub.</p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <span className="rounded-xl border px-4 py-2.5 font-display text-3xl font-extrabold tracking-[0.3em] text-q-text" style={{ borderColor: `rgba(${CYAN},0.4)` }} data-testid="settings-family-code">{code}</span>
        <button type="button" className="q-btn q-btn-secondary" data-testid="settings-copy-code-btn"
          onClick={async () => { if (await copyText(code)) { setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 2500); } }}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? <span data-testid="settings-copied">Copied</span> : "Copy"}
        </button>
      </div>
    </div>
  );
}

function SupportAccess({ hub }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/organizations/${hub.id}/support-access`).then(({ data }) => setData(data)); }, [hub.id]);
  const call = async (fn) => {
    setBusy(true);
    try { setData((await fn()).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!data) return null;
  return (
    <section className="q-card mt-6" data-testid="settings-support-access">
      <h2 className="flex items-center gap-2 font-display text-lg font-bold text-q-text"><LifeBuoy className="h-4 w-4" style={{ color: "rgb(var(--glow-admin))" }} /> Support access</h2>
      <p className="mt-1 text-sm text-q-muted">Let The Queue support see your hub, read-only, so they can help. Every time they open it, it's logged below.</p>
      {data.active ? (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold" style={{ color: "rgb(var(--glow-admin))" }} data-testid="support-access-status">
            Support access on until {new Date(data.until).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
          </span>
          <button className="q-btn q-btn-secondary py-2" disabled={busy} onClick={() => call(() => api.delete(`/organizations/${hub.id}/support-access`))} data-testid="support-access-off-btn">Turn off</button>
        </div>
      ) : (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="mr-2 text-sm text-q-faint" data-testid="support-access-status">Support access is off</span>
          {[["24h", "24 hours"], ["3d", "3 days"], ["7d", "7 days"]].map(([k, l]) => (
            <button key={k} className="q-btn q-btn-secondary py-2" disabled={busy} onClick={() => call(() => api.put(`/organizations/${hub.id}/support-access`, { duration: k }))} data-testid={`support-access-on-${k}`}>{l}</button>
          ))}
        </div>
      )}
      <h3 className="mt-6 text-[10px] font-black uppercase tracking-[0.16em] text-q-faint">Support access log</h3>
      {data.log.length === 0 ? <p className="mt-2 text-sm text-q-faint" data-testid="support-log-empty">No one from support has opened your hub.</p> : (
        <ul className="mt-2 space-y-1.5" data-testid="support-log">
          {data.log.map((e, i) => (
            <li key={i} className="text-sm text-q-muted" data-testid={`support-log-row-${i}`}>
              <span className="font-semibold text-q-text">{e.admin_name}</span> opened support view · {new Date(e.at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HomeCity({ hub, onSaved }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const can = hub.is_parent && !hub.read_only;
  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.get("/weather/geocode", { params: { q } }); setRes(data.results); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  const pick = async (c) => {
    try { await api.put(`/organizations/${hub.id}/home-city`, c); toast.success(`Home city set to ${c.name}`); setRes(null); setQ(""); onSaved(); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <section className="q-card mt-6" data-testid="settings-home-city">
      <h2 className="font-display text-lg font-bold text-q-text">Family's home city</h2>
      <p className="mt-1 text-sm text-q-muted">Used for weather when someone doesn't share their location.</p>
      <p className="mt-3 font-semibold text-q-text" data-testid="settings-home-city-current">{hub.home_city?.name || "Not set"}</p>
      {can && (
        <form onSubmit={search} className="mt-3 flex gap-2" noValidate>
          <input className="q-input" placeholder="Search a city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="settings-home-city-input" />
          <button className="q-btn q-btn-secondary" disabled={busy || q.trim().length < 2} data-testid="settings-home-city-search-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}</button>
        </form>
      )}
      {res && (res.length ? (
        <ul className="mt-2 space-y-1" data-testid="settings-home-city-results">
          {res.map((c, i) => <li key={`${c.name}-${i}`}><button type="button" className="flex min-h-[44px] w-full items-center rounded-lg px-3 text-left text-sm font-semibold text-q-text hover:bg-q-raised" onClick={() => pick(c)} data-testid={`settings-home-city-result-${i}`}>{c.name}</button></li>)}
        </ul>
      ) : <p className="mt-2 text-sm text-q-muted" data-testid="settings-home-city-none">No cities found.</p>)}
    </section>
  );
}

function DeleteHub({ hub }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const go = async () => {
    setBusy(true);
    try {
      await api.delete(`/organizations/${hub.id}/owner-delete`, { data: { confirm_name: name } });
      toast.success(`${hub.name} was deleted`);
      await refresh();
      window.location.assign("/onboarding");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section id="delete-hub" className="q-card mt-6" style={{ borderColor: "rgba(var(--st-red),0.4)" }} data-testid="settings-delete-hub">
      <h2 className="font-display text-lg font-bold text-q-text">Delete {hub.name}</h2>
      <p className="mt-1 text-sm text-q-muted">Removes the whole family hub for everyone: bills, chores, kids' profiles and photos. To keep it, transfer ownership to a co-parent in Members instead.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input className="q-input min-h-[44px] min-w-0 flex-1" placeholder={`Type "${hub.name}"`} value={name} onChange={(e) => setName(e.target.value)} data-testid="delete-hub-name-input" />
        <button className="q-btn min-h-[44px] font-bold text-white" style={{ background: "rgb(220,38,38)" }} disabled={busy || name !== hub.name} onClick={go} data-testid="delete-hub-btn">Delete hub forever</button>
      </div>
    </section>
  );
}

export default function Settings() {
  const { hub, reloadHub } = useHub();
  const { refresh } = useAuth();
  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Family" title="Settings" rgb={CYAN} testid="settings-title" />
      <div className="grid gap-6 lg:grid-cols-2">
        <HubDetails key={hub.id} hub={hub} onSaved={() => { reloadHub(); refresh(); }} />
        <FamilyCode code={hub.family_code} />
      </div>
      <HomeCity hub={hub} onSaved={reloadHub} />
      {hub.is_parent && hub.view_mode === "member" && <FamilySections hub={hub} onChanged={reloadHub} />}
      {hub.my_role !== "owner" && hub.my_role !== "kid" && <PlanNote hub={hub} />}
      {hub.is_parent && hub.view_mode === "member" && <Storage hub={hub} />}
      {hub.is_parent && hub.view_mode === "member" && <SupportAccess hub={hub} />}
      {hub.my_role !== "kid" && <SettingsInboxAddress hub={hub} />}
      {hub.my_role !== "kid" && <YourData hub={hub} />}
      {hub.my_role === "owner" && hub.view_mode === "member" && <DeleteHub hub={hub} />}
      <section className="q-card mt-6" data-testid="settings-about">
        <h2 className="font-display text-lg font-bold text-q-text">About</h2>
        <div className="mt-5 flex items-center gap-4">
          <HouseQ size={56} />
          <div>
            <Wordmark className="text-2xl" />
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.38em] text-q-muted">Software for you</div>
          </div>
        </div>
        <p className="mt-5 text-sm text-q-muted">The Queue Home Hub · Phase 1 · the-queue.com</p>
        <p className="mt-4 border-t border-q-border pt-4 text-[11px] leading-relaxed text-q-faint" data-testid="legal-footer">{LEGAL_TEXT}</p>
      </section>
    </div>
  );
}

function SettingsInboxAddress({ hub }) {
  const inbox = useInbox(hub.id);
  if (inbox.denied || !inbox.d) return null;
  return <div className="mt-6"><InboxAddressCard hubId={hub.id} inbox={inbox} canWrite={!hub.read_only} testid="settings-inbox-address" /></div>;
}

function PlanNote({ hub }) {
  const p = usePlan(hub.id);
  if (!p?.name) return null;
  return (
    <section className="q-card mt-6 p-4" data-testid="settings-plan">
      <h2 className="font-display text-lg font-bold text-q-text">Plan</h2>
      <p className="text-sm text-q-text" data-testid="settings-plan-name">{p.name}</p>
      <p className="text-sm text-q-muted" data-testid="settings-plan-ask">Ask {p.owner_name} about changing plans.</p>
    </section>
  );
}
